<template>
  <div id="userInputs">
    <VpaCalculator v-on:useValue="vpa = $event" v-on:closeModal="showVpaCalculator = false" v-if="showVpaCalculator == true" />

    <form @submit.prevent>
      <div class="slider-field mt-1">
        <label>Vines Per Acre <i v-b-tooltip.hover="'Enter the the rough number of vines per acre on your property.'" class="far fa-question-circle clickable"></i></label>
        <div class="row">
          <div class="col-8">
            <!-- VPA -->
            <vue-slider class="mt-1" v-model.number="vpa" :tooltip="false" :min="300" :max="1000"></vue-slider>
          </div><!-- /.col-8 -->
          <div class="col-4">
            <input v-model.number="vpa" type="number" class="form-control" />
          </div><!-- /.col-4 -->
        </div><!-- /.row -->
        <button @click="showVpaCalculator = true" class="btn btn-sm btn-outline-light mt-1 rounded-pill"><i class="fas fa-calculator"></i> Calculate My Vines/Acre</button>
      </div><!-- /.slider-field -->

      <!-- MANAGEMENT ZONES -->
      <div v-for="(zone, index) in mzs" class="card mt-2 zone-card">
        <p class="text-center"><strong>Management Zone {{ index + 1}}</strong></p>
        <div v-bind:class="{'is-invalid': calcAreaPctTotal != 100}" class="slider-field mb-1">
          <label>Area Percentage <i v-b-tooltip.hover="'Enter the percentage of area taken up by this zone. All zones must add up to 100%'" class="far fa-question-circle clickable"></i></label>
          <div class="row">
            <div class="col-8">
              <!-- ZONE INPUTS -->
              <vue-slider class="mt-1" v-model.number="zone.areaPct" :tooltip="false"></vue-slider>
            </div><!-- /.col-8 -->
            <div class="col-4">
              <input v-model.number="zone.areaPct" type="number" class="form-control" /><span class="units">%</span>
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- /.slider-field -->

        <div class="slider-field mb-1">
          <label>Beginning Mean Vine Size (Pruning Weight) <i v-b-tooltip.hover="'Mean Vine Size in Lbs/vine in this zone. Mean vine size is the weight of pruned matter grown the previous year. Only weight fruit bearing vine matter should be measured.'" class="far fa-question-circle clickable"></i></label>
          <div class="row">
            <div class="col-8">
              <!-- ZONE INPUTS -->
              <vue-slider class="mt-1" v-model.number="zone.yrOneMvs" :tooltip="false" :min="0" :max="10" :interval="0.1"></vue-slider>
            </div><!-- /.col-8 -->
            <div class="col-4">
              <input v-model.number="zone.yrOneMvs" type="number" class="form-control" /><span class="units">lbs/vine</span>
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- /.slider-field -->

        <div class="slider-field mb-1">
          <label>Year One Crop Estimation <i v-b-tooltip.hover="'Your crop estimation this year before variable rate management.'" class="far fa-question-circle clickable"></i></label>
          <div class="row">
            <div class="col-8">
              <!-- ZONE INPUTS -->
              <vue-slider class="mt-1" v-model.number="zone.yrOneCropEst" :tooltip="false" :min="0" :max="20" :interval="0.1"></vue-slider>
            </div><!-- /.col-8 -->
            <div class="col-4">
              <input v-model.number="zone.yrOneCropEst" type="number" class="form-control" /><span class="units">tons/acre</span>
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- /.slider-field -->

        <div v-bind:class="{'is-invalid': zone.desClVrm > calcYrOneCl(index)}" class="slider-field mb-1">
          <p v-if="zone.desClVrm > calcYrOneCl(index)"><strong>** This zone's desired crop load (Ravaz) cannot be greater than this zone's year one crop load (Ravaz). Current max desired Crop Load is {{ calcYrOneCl(index) | roundTwoPlaces }} **</strong></p>
          <label>Desired Crop Load (Ravaz Index) <i v-b-tooltip.hover="'Enter your desired crop load here. 10 to 14 is considered balanced. This number is a proportion of yeild weight over pruning weight. Also known as the Ravaz index. Cannot be greater than estimated crop load for year 1.'" class="far fa-question-circle clickable"></i></label>
          <div class="row">
            <div class="col-8">
              <!-- ZONE INPUTS -->
              <vue-slider class="mt-1" v-model.number="zone.desClVrm" :tooltip="false" :min="5" :max="22" :interval="0.1"></vue-slider>
            </div><!-- /.col-8 -->
            <div class="col-4">
              <input v-model.number="zone.desClVrm" type="number" class="form-control" />
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- /.slider-field -->

        <!-- END ZONE INPUTS -->

        <!-- ZONE BUTTONS -->
        <div>
          <button @click="zone.modalClass = 'show'" class="btn btn-sm btn-outline-light mt-1 rounded-pill"><i class="far fa-chart-bar"></i> Show Details</button>
          <button @click="removeZone(index)" class="btn btn-sm btn-outline-light mt-1 rounded-pill"><i class="far fa-trash-alt"></i></button>
        </div><!-- /div to make button group behave -->
        <!-- END ZONE BUTTONS -->

        <!-- ZONE DETAILs MODAL -->
        <div class="orbitist-modal" :class="zone.modalClass">
          <div class="orbitist-modal-container">
            <button @click="zone.modalClass = 'hidden'" class="btn btn-sm btn-outline-dark rounded-pill float-right"><i class="fas fa-times"></i> Close</button>
            <p><strong>Management Zone {{ index + 1}}</strong></p>
            <!-- TABLE -->
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Year</th>
                  <th scopt="col">No VRM</th>
                  <th scope="col">With VRM</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="col">1</th>
                  <th scopt="col">
                    <p>Year One Crop Load (Ravaz): {{ calcYrOneCl(index) | roundTwoPlaces }} yield weight/pruning weight</p>
                    <p>Year One Juice Soluble Solids (Brix): {{ calcYrOneJss(index) | roundTwoPlaces }}%</p>
                  </th>
                  <th scope="col">
                    <p>Year One Model Target Crop with VRM: {{ calcYrOneMdlTrgCropVrm(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year One Target Crop with VRM: {{ calcYrOneTrgCropVrm(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year One Target Crop Change with VRM: {{ calcYrOneTrgCropVrmChg(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year One Crop with VRM: {{ calcYrOneCropVrm(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year One Crop Load (Ravaz) with VRM: {{ calcYrOneClVrm(index) | roundTwoPlaces }} yield weight/pruning weight</p>
                    <p>Year One Juice Soluble Solids (Brix) with VRM: {{ calcYrOneJssVrm(index) | roundTwoPlaces }}%</p>
                  </th>
                </tr>
                <tr>
                  <th scope="col">2</th>
                  <th scopt="col">
                    <p>Year Two Change in Mean Vine Size: {{ calcYrTwoMvsChg(index) | roundTwoPlaces }} lbs/vine</p>
                    <p>Year Two Crop Potential: {{ calcYrTwoCropPot(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year Two Crop Potential Change: {{ calcYrTwoCropPotChg(index) | roundTwoPlaces }} tons/acre</p>
                  </th>
                  <th scope="col">
                    <p>Year Two Mean Vine Size with VRM: {{ calcYrTwoMvsVrm(index) | roundTwoPlaces }} lbs/vine</p>
                    <p>Year Two Mean Vine Size Change with VRM: {{ calcYrTwoMvsVrmChg(index) | roundTwoPlaces }} lbs/vine</p>
                    <p>Year Two Mean Vine Size with VRM: {{ calcYrTwoMvsVrm(index) | roundTwoPlaces }} lbs/vine</p>
                    <p>Year Two Crop Potential with VRM: {{ calcYrTwoCropPotVrm(index) | roundTwoPlaces }} tons/acre</p>
                    <p>Year Two Crop Potential with VRM Change: {{ calcYrTwoCropPotVrmChg(index) | roundTwoPlaces }} tons/acre</p>
                  </th>
                </tr>
              </tbody>
            </table>
          </div><!-- /.orbitist-modal-container -->
      </div><!-- /.orbitist-modal -->
        <!-- END ZONE MODAL -->


      </div><!-- /.card -->
      <!-- END MANAGEMENT ZONES -->

      <button @click="addZone()" class="btn btn-sm btn-outline-light rounded-pill btn-block mt-2 mb-4"><i class="fas fa-plus"></i> Add Management Zone</button>

    </form>
  </div><!-- /#userInputs -->
</template>

<script>
import { mapState } from 'vuex'
import { mapGetters } from 'vuex'
import vueSlider from 'vue-slider-component'
import VpaCalculator from './VpaCalculator'

export default {
  components: {
    vueSlider,
    VpaCalculator
  },
  data() {
    return {
      showVpaCalculator: false
    }
  },
  computed: {
    ...mapState([
      'mzs',
      'vpa'
    ]),
    ...mapGetters([
      'calcAreaPctTotal',
      'calcYrOneCl',
      'calcYrOneJss',
      'calcYrTwoMvsChg',
      'calcYrOneMdlTrgCropVrm',
      'calcYrTwoMvsVrm',
      'calcYrTwoCropPot',
      'calcYrTwoCropPotChg',
      'calcYrOneTrgCropVrm',
      'calcYrOneTrgCropVrmChg',
      'calcYrOneCropVrm',
      'calcYrOneClVrm',
      'calcYrOneJssVrm',
      'calcYrTwoMvsVrmChg',
      'calcYrTwoCropPotVrm',
      'calcYrTwoCropPotVrmChg'
    ]),
    vpa: {
      get() {
        return this.$store.state.vpa
      },
      set(value) {
        this.$store.commit('setVpa', value)
      }
    }
  },
  methods: {
    addZone() {
      this.mzs.push(
        {
          areaPct: 0,
          yrOneMvs: 0,
          yrOneCropEst: 0,
          desClVrm: 0,
          modalClass: 'hidden'
        }
      )
    },
    removeZone(i) {
      if (confirm('Are you sure you want to remove this zone?')) {
        this.mzs.splice(i, 1);
      } else {
        console.log('Cancelled!')
      }
    }
  },
  filters: {
    roundTwoPlaces(value) {
      return value.toFixed(2)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/UserInputs.vue